import algoliasearch from 'algoliasearch/lite';
import { buildBookingAutoIcon, removeMarkerHighlight } from './map-utils';
import { ajax, dateToString, debounced, dynamicSort, getMapRadius } from '../utils';
import noUiSlider from 'nouislider';
import 'nouislider/distribute/nouislider.css';
import flatpickr from "flatpickr";
import flatpickrLanguages from "flatpickr/dist/l10n";
import Wish from "./Wish";
import { initSwiper } from "../../modules/common/init-swiper";
import { initOffuscation } from "../../modules/frontv2/link-offuscation";

class MapHostSearch{
  constructor(wrapper = document, mapboxgl = undefined){
    this.wrapper = wrapper;
    if(this.wrapper){
      this.mapContainer = this.wrapper.querySelector('#map-container');
      this.hostsContainer = this.wrapper.querySelector('#hosts-container');
      this.searchWrapper = this.wrapper.querySelector('#search-wrapper');
      if(this.searchWrapper && this.mapContainer && this.hostsContainer && typeof mapboxgl !== "undefined"){
        this.mapboxgl = mapboxgl;
        this.currentDateSelected = null; //init date selected

        //debounced search to save algolia operations / and for perf
        this.debouncedPerformSearch = debounced(300, () => {
          this.performSearch();
        });

        //culture types to get the translation through data attributes
        this.cultureTypes = ['data-biodynamic', 'data-organic', 'data-reasoned'];

        this.markersPlaced = []; //mapbox markers placed on map
        this.currencyInfos = { //all currency infos needed to build prices on list/popups
          locale: document.body.dataset.currencyLocale.replace("_", "-"),
          rate: document.body.dataset.currencyRate,
          iso: document.body.dataset.currencyIso
        };
        // this.mobileCardsContainer = this.mapContainer.querySelector('#mobilePopupsContainer');
        this.zoomInButton = this.mapContainer.querySelector('#zoomIn');
        this.zoomOutButton = this.mapContainer.querySelector('#zoomOut');

        this.minPrice = 0;
        this.maxPrice = this.hostsContainer.dataset.highestPrice; //max price for the price slider filter

        // this.searchOnMoveCheckbox = this.wrapper.querySelector('#searchOnMove');
        this.triggerManualSearchButton = this.wrapper.querySelector('#triggerSearch');

        this.hostsMapContainer = this.wrapper.querySelector('#hosts-map-container');

        this.lastParams = {};
        this.initialLoadDone = false; //know if initial load was done or not
        // this.playOpenAnimation = true; //by default, plays the animation > when a marker is clicked, scrolls to host list card

        //used to check which message block to show on top of request fallback results
        this.mainFiltersActive = false;
        this.secondaryFiltersActive = false;
        this.mainActiveFiltersList = [];

        this.initMap();
        this.initPriceSlider();
        this.initDatepickr();

        //init text on active filters on top bar
        this.initPersonsActiveFilter();
        this.initPriceActiveFilter();
        this.initLangsActiveFilter();
        this.initTypeOfExpActiveFilter();
        this.initReviewsFilter();

        this.overlayFilters = this.wrapper.querySelector('#overlay-filters');
        this.filterButtons = this.wrapper.querySelectorAll('.btn-dropdown:not(.sort)');
        if(this.overlayFilters && this.filterButtons){
          this.initOverlay();
        }

        this.moreFiltersButton = this.wrapper.querySelector('#moreFilters');
        this.moreFiltersWindow = this.wrapper.querySelector('#filters');
        if(this.moreFiltersButton && this.moreFiltersWindow){
          this.initMoreFiltersToggle();

          if(this.moreFiltersWindow.hasAttribute('data-mobile')){
            this.moreFiltersCountActiveElement = this.moreFiltersButton.querySelector('.active-filters');
            if(this.moreFiltersCountActiveElement){
              this.initComputeActiveFilters();
            }
          }
        }

        this.initClearButtons();
        this.initSaveButtons();

        //toggle map/list in mobile/tablet on button click
        this.toggleLayoutButton = this.wrapper.querySelector('#toggleLayout');
        this.toggleLayoutWrapper = this.wrapper.querySelector('#show-map-list-wrapper');
        this.iconToggleLayout = this.wrapper.querySelector('#icon-toggle-map');
        if(this.toggleLayoutButton && this.toggleLayoutWrapper){
          this.initToggleMap();
        }

        //centers the map on the user position when clicking the button
        this.triggerAroundMeButton = this.wrapper.querySelector('#trigger-around-me');
        if(this.triggerAroundMeButton){
          this.aroundMeMarkerPlaced = false;
          this.initAroundMe();
        }

        //close more filters on buttons click
        this.closeFiltersButtons = this.wrapper.querySelectorAll('.closeFilters');
        if(this.closeFiltersButtons){
          this.initCloseMoreFiltersButtons();
        }

        this.sortButtonText = this.wrapper.querySelector('#sort-button span');
        this.sortOptions = this.wrapper.querySelectorAll('.dropdown-container.sort input');
        this.sortOption = 'popularity'; //server side base sort
        if(this.sortButtonText && this.sortOptions){
          this.initSorting();
        }

        //add/remove the card highlight depending on mouse hover
        this.initCardMouseHover();

        //On kiosk, add a blue point at the center of the map
        if(this.searchWrapper.hasAttribute('data-kiosk')){
          this.initCenterPointOnKiosk();
        }
      }
    }
  }

  initPriceSlider(){
    const _this = this;

    const priceSlider = this.wrapper.querySelector('#price-slider');
    if(priceSlider){
      noUiSlider.create(priceSlider, {
        start: [0, parseInt(this.hostsContainer.dataset.highestPrice)],
        connect: true,
        tooltips: [true, true],
        range: {
          'min': 0,
          'max': parseInt(this.hostsContainer.dataset.highestPrice)
        },
        format: {
          to: function (value) {
            let formattedPrice = new Intl.NumberFormat(_this.currencyInfos.locale, {
              style: 'currency',
              currency: _this.currencyInfos.iso,
              minimumFractionDigits: 0 })
              .format(parseInt(value) * _this.currencyInfos.rate);

            return formattedPrice;
          },
          from: function (value) {
            return parseInt(value);
          }
        }
      });
    }
  }


  initDatepickr(){
    flatpickr.localize(flatpickrLanguages[document.body.dataset.lang.substr(0, 2)]);

    this.flatpickrInstance = null;
    this.inlineDate = this.wrapper.querySelector("#inlineDate");

    //on desktop, init flatpickr when clicking on the filter that displays the calendar
    //otherwise, the init doesn't work correctly since the calendar is not visible
    let defaultDate = new Date();
    if(this.wrapper.dataset.date && this.wrapper.dataset.date !== 'false'){
      defaultDate = new Date(this.wrapper.dataset.date);
    }
    if (this.inlineDate) {
      this.dateWrapper = this.inlineDate.parentElement;

      this.initFlatpickrInstance(defaultDate);
      this.flatpickrInitedAtClick = false;
      this.inlineDate.addEventListener("click", () => {
        if(!this.flatpickrInitedAtClick){
          this.initFlatpickrInstance(defaultDate);
          this.flatpickrInitedAtClick = true;
        }
      });
    }
    //on mobile, just init the flatpickr
    else{
      this.dateWrapper = this.wrapper.querySelector('#date-wrapper');
      this.initFlatpickrInstance(defaultDate);
    }
  }

  initFlatpickrInstance(defaultDate){
    const _this = this;

    const flatpickrElt = _this.wrapper.querySelector(".flatpickr");
    if (flatpickrElt/* && !_this.flatpickrInstance*/) {
      let minDate = new Date();
      minDate.setHours(0, 0, 0);

      _this.flatpickrInstance = flatpickr(flatpickrElt, {
        inline: true,
        minDate: minDate,
        dateFormat: "Y-m-d",
        defaultDate: defaultDate,
        showMonths: flatpickrElt.dataset.showMonths,
        // mode: "range",
        onChange: function (selectedDates) {
          if (selectedDates.length && selectedDates[0]) {
            _this.currentDateSelected = selectedDates[0];

            //if we are on a kiosk, save the date filter into session for global filters
            if(_this.searchWrapper.hasAttribute('data-kiosk')){
              _this.globalFilterDate = new Date(selectedDates[0]);
              _this.updateSession();
              // window.localStorage.setItem('kioskDateFilter', selectedDates[0]);
            }

            const locale = document.body.dataset.lang.replace("_", "-");
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            const date = new Date(selectedDates[0]).toLocaleDateString(locale, options);

            let daysOfWeek = JSON.stringify([selectedDates[0].getDay()]);
            _this.dateWrapper.dataset.activeDays = daysOfWeek;

            if(_this.inlineDate){
              _this.inlineDate.textContent = date;
              _this.inlineDate.classList.add("active");
            }

            //recompute nb of active filters
            if(_this.moreFiltersCountActiveElement){
              _this.computeActiveFiltersCount();
            }
          }
        },
        onReady: function (a, b, f) {
          if (_this.wrapper.hasAttribute('data-date') && _this.wrapper.dataset.date !== 'false'){
            f.setDate(f.config.defaultDate, true);
          }

          //init the clear button
          _this.clearDate = _this.wrapper.querySelector('#clear-date');
          if(_this.clearDate){
            _this.clearDate.addEventListener('click', () => {
              f.clear();

              //in case we're on a kiosk
              //delete the date filter from localstorage
              if(_this.searchWrapper.hasAttribute('data-kiosk')){
                _this.globalFilterDate = null;
                _this.updateSession();
                // window.localStorage.removeItem('kioskDateFilter');
              }

              if(_this.inlineDate){
                _this.inlineDate.textContent = _this.inlineDate.dataset.initialText;
                _this.inlineDate.classList.remove("active");
              }

              delete _this.dateWrapper.dataset.activeDays;

              _this.clearDateParent = _this.clearDate.closest('.dropdown');
              if(_this.clearDateParent){
                _this.clearDateParent.classList.remove('active');
              }

              _this.currentDateSelected = null;
              _this.removeOverlay();

              //recompute nb of active filters
              if(_this.moreFiltersCountActiveElement){
                _this.computeActiveFiltersCount();
              }
            });
          }
        }
      });
    }
  }

  //handle active text on filters
  initPersonsActiveFilter(){
    this.adults = 0;
    this.children = 0;
    this.adultsInput = this.wrapper.querySelector('#number-adults');
    this.childrenInput = this.wrapper.querySelector('#number-children');

    this.personsText = this.wrapper.querySelector('#inlinePersons');
    this.personsInput = this.wrapper.querySelectorAll('.personsInput');
    if(this.personsInput){
      this.personsInput.forEach(input => {
        input.addEventListener('change', () => {
          if(input.dataset.type == 'adult') this.adults = input.value;
          else if(input.dataset.type == 'child') this.children = input.value;

          if(this.personsText){
            let text = "";
            if (this.adultsInput.value > 0) {
              let adultsTrad = this.personsText.dataset.adults.replace("{0}", this.adultsInput.value);
              let adultTrad = this.personsText.dataset.adult;
              text += this.adultsInput.value > 1 ? adultsTrad : adultTrad;
            }
            if (this.childrenInput.value > 0) {
              if (this.adultsInput.value > 0) {
                text += ", ";
              }
              let childrenTrad = this.personsText.dataset.children.replace("{0}", this.childrenInput.value);
              let childTrad = this.personsText.dataset.child;
              text += this.childrenInput.value > 1 ? childrenTrad : childTrad;
            }

            if (this.adultsInput.value == 0 && this.childrenInput.value == 0) {
              text = this.personsText.dataset.noValue;
              this.personsText.classList.remove("active");
            }
            else {
              this.personsText.classList.add("active");
            }

            this.personsText.textContent = text;
          }

          //on kiosk, update session vars for global filters
          if(this.searchWrapper.hasAttribute('data-kiosk')){
            this.updateSession();
          }
        });

        input.dispatchEvent(new Event('change'));
      });
    }
  }

  initPriceActiveFilter(){
     this.inlinePriceSlider = this.wrapper.querySelector("#price-slider");
    if (this.inlinePriceSlider) {
      this.inlinePriceDisplay = this.wrapper.querySelector("#inline-price-text");
      this.inlinePriceSlider.noUiSlider.on("change", (values, handle, unencoded) => {
        this.minPrice = parseInt(unencoded[0]);
        this.maxPrice = parseInt(unencoded[1]);

        if (this.inlinePriceDisplay) {
          this.inlinePriceDisplay.textContent = values[0] + " • " + values[1];
          this.inlinePriceDisplay.classList.add("active");
        }

        if(this.moreFiltersCountActiveElement){
          this.computeActiveFiltersCount();
        }
      });
    }
  }

  initLangsActiveFilter(){
    this.activeLangCheckboxes = this.wrapper.querySelectorAll('.lang-filter:checked').length;
    this.langCheckboxes = this.wrapper.querySelectorAll('.lang-filter');
    this.langFilterText = this.wrapper.querySelector('#lang-active-text');
    this.langs = [];
    if(this.langCheckboxes && this.langFilterText){
      this.langCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          if(checkbox.checked) this.activeLangCheckboxes++;
          else this.activeLangCheckboxes--;

          this.handleLangsActiveText();

          //in case of kiosk, update session for global filters
          if(this.searchWrapper.hasAttribute('data-kiosk')){
            const activeLangs = this.wrapper.querySelectorAll('.lang-filter:checked');
            if(activeLangs){
              this.langs = [];
              activeLangs.forEach(lang => {
                this.langs.push(lang.dataset.id);
              });

              this.updateSession();
            }
          }
        });
      });

      this.handleLangsActiveText();
    }
  }

  handleLangsActiveText(){
    if(this.activeLangCheckboxes > 0){
      this.langFilterText.textContent = this.langFilterText.dataset.initialText + ' • ' + this.activeLangCheckboxes;
      this.langFilterText.classList.add('active');
    }
    else{
      this.langFilterText.textContent = this.langFilterText.dataset.initialText;
      this.langFilterText.classList.remove('active');
    }
  }

  initTypeOfExpActiveFilter(){
    this.activeTypeOfExpCheckboxes = this.wrapper.querySelectorAll('.type-of-exp-filter:checked').length;
    this.typeOfExpCheckboxes = this.wrapper.querySelectorAll('.type-of-exp-filter');
    this.typeOfExpFilterText = this.wrapper.querySelector('#type-of-exp-text');
    if(this.typeOfExpCheckboxes && this.typeOfExpFilterText){
      this.typeOfExpCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          if(checkbox.checked) this.activeTypeOfExpCheckboxes++;
          else this.activeTypeOfExpCheckboxes--;

          this.handleActiveTypeOfExpText();
        });
      });

      this.handleActiveTypeOfExpText();
    }
  }

  initReviewsFilter(){
    let containerReviews = document.getElementById("container-reviews");
    if(!containerReviews) return;

    let buttons = containerReviews.querySelectorAll("button");
    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        buttons.forEach((button) => {
          button.classList.remove("active");
        });
        button.classList.add("active");
      });
    });
  }

  handleActiveTypeOfExpText(){
    if(this.activeTypeOfExpCheckboxes > 0){
      this.typeOfExpFilterText.textContent = this.typeOfExpFilterText.dataset.initialText + ' • ' + this.activeTypeOfExpCheckboxes;
      this.typeOfExpFilterText.classList.add('active');
    }
    else{
      this.typeOfExpFilterText.textContent = this.typeOfExpFilterText.dataset.initialText;
      this.typeOfExpFilterText.classList.remove('active');
    }
  }

  initOverlay(){
    this.filterButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.displayOverlay();
        button.parentElement.classList.add('z-50');

        if(!button.parentElement.classList.contains('active')){
          // this.overlayFilters.classList.add('hidden');
          // this.overlayFilters.classList.remove('z-40');
          this.removeOverlay();
        }
      });
    });

    this.overlayFilters.addEventListener('click', () => {
      this.removeOverlay();
      this.removeMoreFilters();
    });
  }

  initMoreFiltersToggle(){
    this.moreFiltersButton.addEventListener('click', () => {
      this.displayOverlay();
      this.displayMoreFilters();
    });
  }

  // toggleMoreFilters(){
  //   this.moreFiltersWindow.classList.toggle('opened');
  // }

  displayMoreFilters(){
    this.moreFiltersWindow.classList.add('opened');
  }

  removeMoreFilters(){
    this.moreFiltersWindow.classList.remove('opened');
  }

  displayOverlay(){
    this.overlayFilters.classList.remove('hidden');
  }

  removeOverlay(){
    this.overlayFilters.classList.add('hidden');
    this.debouncedPerformSearch();

    const dropdownFilters = this.wrapper.querySelectorAll('.dropdown-filter.z-50');
    if(dropdownFilters) dropdownFilters.forEach(dropdownFilter => dropdownFilter.classList.remove('z-50'));
  }

  initClearButtons(){
    this.clearLanguagesButton = this.wrapper.querySelector('#clear-languages');
    if(this.clearLanguagesButton){
      this.clearLanguagesButton.addEventListener('click', () => {
        this.activeLangCheckboxes = 0;
        this.langCheckboxes.forEach(checkbox => checkbox.checked = false);
        this.langFilterText.classList.remove('active');
        this.langFilterText.textContent = this.langFilterText.dataset.initialText;

        const dropdown = this.clearLanguagesButton.closest('.dropdown.active');
        if(dropdown) dropdown.classList.remove('active');

        this.removeOverlay();

        if(this.searchWrapper.hasAttribute('data-kiosk')){
          this.langs = [];
          this.updateSession();
        }
      });
    }

    this.clearTypesOfExpButton = this.wrapper.querySelector('#clear-types-of-exp');
    if(this.clearTypesOfExpButton){
      this.clearTypesOfExpButton.addEventListener('click', () => {
        this.activeTypeOfExpCheckboxes = 0;
        this.typeOfExpCheckboxes.forEach(checkbox => checkbox.checked = false);
        this.typeOfExpFilterText.textContent = this.typeOfExpFilterText.dataset.initialText;
        this.typeOfExpFilterText.classList.remove('active');

        const dropdown = this.clearTypesOfExpButton.closest('.dropdown.active');
        if(dropdown) dropdown.classList.remove('active');

        this.removeOverlay();
      });
    }
  }

  clearPrice(){
    this.maxPrice = this.hostsContainer.dataset.highestPrice;
    this.minPrice = 0;

    this.inlinePriceSlider.noUiSlider.set([this.minPrice, this.maxPrice]);

    if(this.inlinePriceDisplay){
      this.inlinePriceDisplay.textContent = this.inlinePriceDisplay.dataset.initialText;
      this.inlinePriceDisplay.classList.remove('active');
    }
  }

  clearPersons(){
    this.adultsInput.value = 0;
    this.childrenInput.value = 0;

    this.personsInput.forEach(input => {
      input.dispatchEvent(new Event('change'));
    });
  }

  clearLangs(){
    if(this.clearLanguagesButton){
      this.clearLanguagesButton.dispatchEvent(new Event('click'));
    }
    else{
      this.langCheckboxes.forEach(checkbox => checkbox.checked = false);
    }
  }

  initClearDate(){
    const clearDateButton = this.wrapper.querySelector('#clear-date');
    if(clearDateButton) clearDateButton.dispatchEvent(new Event('click'));
  }

  clearTypeOfExp(){
    if(this.clearTypesOfExpButton){
      this.clearTypesOfExpButton.dispatchEvent(new Event('click'));
    }
    else{
      this.typeOfExpCheckboxes.forEach(checkbox => checkbox.checked = false);
    }
  }

  clearSecondaryFilters(){
    const filtersToReset = this.wrapper.querySelectorAll(".filter:checked");
    if (filtersToReset) {
      filtersToReset.forEach(function (filterToReset) {
        filterToReset.checked = false;
      });
    }

    this.instantBook.checked = false;

    const visibilityElt = this.wrapper.querySelector('#more-both');
    if(visibilityElt) visibilityElt.checked = true;

    const cancellationElt = this.wrapper.querySelector('#cancellation-all');
    if(cancellationElt) cancellationElt.checked = true;

    // Filter reviews
    const containerReviews = document.getElementById('container-reviews');
    const buttons = containerReviews.querySelectorAll('button');
    buttons.forEach((button) => {
      button.classList.remove('active');
    });
    buttons[0].classList.add('active');
  }

  initSaveButtons(){
    this.saveButtons = this.wrapper.querySelectorAll('.save');
    if(this.saveButtons){
      this.saveButtons.forEach(button => {
        button.addEventListener('click', () => {
          button.closest('.dropdown.active').classList.remove('active');

          this.removeOverlay();
        });
      });
    }
  }

  initMap(){
    this.map = new this.mapboxgl.Map({
      accessToken: 'pk.eyJ1Ijoid2luYWxpc3QiLCJhIjoiY2p0azd3b2Q5MTQzMjN5bzZidmU5cDF6biJ9.65pAPF4tbn5Geqg7JhvCFQ',
      name: 'Winalist',
      container: 'map-container',
      style: 'mapbox://styles/winalist/cjtk7v3a4a4q11fpe37hd6yje',
      zoom: this.mapContainer.dataset.zoom ?? 8,
      center: [this.mapContainer.dataset.lng, this.mapContainer.dataset.lat], //default to Champagne lat/lng,
      dragPan: !this.searchWrapper.hasAttribute('data-kiosk'),
      doubleClickZoom: !this.searchWrapper.hasAttribute('data-kiosk'),
      touchZoomRotate: !this.searchWrapper.hasAttribute('data-kiosk')
    });
    this.map.scrollZoom.disable();

    this.bindZoomButtons();

    this.algolia = this.getAlgoliaClient();

    // this.map.on("zoomend", () => {
    //     this.performSearch();
    // });

    this.map.on('dragend', () => {
      // if(this.searchOnMoveCheckbox.checked)
      //   this.debouncedPerformSearch();
      // else{
        if(this.triggerManualSearchButton.classList.contains('hidden')){
          this.triggerManualSearchButton.classList.remove('hidden');
        }
      // }
    });

    this.map.on('moveend', () => {
      // if(this.searchOnMoveCheckbox.checked)
      //   this.debouncedPerformSearch();
      // else{
        if(this.triggerManualSearchButton.classList.contains('hidden')){
          this.triggerManualSearchButton.classList.remove('hidden');
          // this.searchOnMoveCheckbox.classList.add('hidden');
        }
      // }
    });

    this.triggerManualSearchButton.addEventListener('click', () => {
      this.debouncedPerformSearch();
      this.triggerManualSearchButton.classList.add('hidden');
      // this.searchOnMoveCheckbox.classList.remove('hidden');
    });

    this.map.on('click', () => {
      removeMarkerHighlight();
    });

    //if needed, place a marker at a point lat/lng
    //for now, used on cities landing for city location
    if(this.wrapper.hasAttribute('data-point-lat') && this.wrapper.hasAttribute('data-point-lng')){
      let div = document.createElement('div');
      div.className = 'location-marker';
      new this.mapboxgl.Marker({
        element: div
      })
      .setLngLat([this.wrapper.dataset.pointLng, this.wrapper.dataset.pointLat])
      .addTo(this.map);
    }

    //On initial load, we use hosts loaded from server side
    if(!this.initialLoadDone){
      //get the json with hosts loaded from back (used once so no need to store it in this)
      const hostsJson = document.getElementById('hosts-json');
      if(hostsJson){
        this.hosts = JSON.parse(hostsJson.innerHTML);
        this.updateMarkersOnMap(); //updates markers on map

        if(this.hosts.length === 0){
          this.hostsContainer.textContent = this.hostsContainer.dataset.noHostsFound;
          this.hostsContainer.classList.add('font-semibold');
        }
      }

      this.initialLoadDone = true;
    }

    if(this.searchWrapper.hasAttribute('data-trigger-search-on-page-load')){
      this.debouncedPerformSearch();
    }
  };

  bindZoomButtons(){
    if(this.map && this.zoomInButton && this.zoomOutButton){
      this.zoomInButton.addEventListener('click', () => {
        this.map.zoomIn();
      });
      this.zoomOutButton.addEventListener('click', () => {
        this.map.zoomOut();
      });
    }
  }

  getAlgoliaClient(){
    const env = document.body.dataset.env;
    const lang = document.body.dataset.lang.substr(0, 2);

    let prefix = '';
    if(env === 'localhost' || env == 'staging'){
      prefix = 'localhost_';
    }
    let experiences_index = prefix + 'experiences_' + lang;

    const client = algoliasearch('AK7I6BLE3J', '6b23703cf5bc843f0015aa31634bdeef');
    return client.initIndex(experiences_index);
  }

  //Perform the search through algolia
  performSearch(){
    let filters = this.getActiveFilters();
    let params = {
      query: '',
      hitsPerPage: 200,
      attributesToRetrieve: '*',
      responseFields: '*',
      numericFilters: filters.numericFilters,
      facetFilters: filters.facetFilters
    };

    let pos = this.map.getCenter();
    params.aroundLatLng = pos.lat + "," + pos.lng;
    let bounds = this.map.getBounds();
    params.aroundRadius = getMapRadius(bounds._ne, bounds._sw);

    const _this = this;

    //make sure to not trigger a search with the same params as before to avoid useless algolia operations
    if(JSON.stringify(this.lastParams) !== JSON.stringify(params)){
      //add overlay showing that a search is ocurring
      this.hostsContainer.classList.add('overlay-on');

      //get experiences found with experiences.hits
      this.algolia.search(params, (err, experiences) => {
        _this.searchDone(err, experiences, params);
      });
    }
  }

  //isFallbackRequest is the request made if no hosts are found with1 the initial filters
  searchDone(err, experiences, params, isFallbackRequest = false){
    if(!isFallbackRequest)
      this.lastParams = params;

    if (err) throw err;

    if(!isFallbackRequest && this.currentDateSelected !== null){ //if user chose a date filter
      this.checkAvailability(experiences.hits); //we check if every experience is actually available (has at least one timeslot opened at the specified date)
    }

    //TODO > remove
    // experiences.hits.forEach(experience => experience.bookedTimes = Math.floor(Math.random() * 500));

    //since we filter on experiences but we need to display hosts
    //filter here to get only hosts
    this.hosts = [];
    experiences.hits.forEach((experience) => {
      if(typeof experience.host.id !== 'undefined'){
        if(typeof this.hosts[experience.host.id] === 'undefined'){
          this.hosts[experience.host.id] = experience.host;
          this.hosts[experience.host.id]['can_book_auto'] = experience.booking_auto;
          this.hosts[experience.host.id]['bookedTimes'] = experience.bookedTimes;
        }
        else{
          if(experience.booking_auto !== 0) {
            this.hosts[experience.host.id]['can_book_auto'] = experience.booking_auto;
          }
          this.hosts[experience.host.id]['bookedTimes'] += experience.bookedTimes;
        }

        // Attributes for slider host images
        if (typeof this.hosts[experience.host.id].experiences === 'undefined') {
          this.hosts[experience.host.id].experiences = [];
        }

        this.hosts[experience.host.id].experiences.push({
          name: experience.name,
          coverCdnUrl: experience.cover_cdn_url,
          coverCdnPath: experience.cover_cdn_path
        });
      }
    });

    if(!isFallbackRequest && this.hosts.length == 0){
      let params = this.getBlankParams();
      return this.algolia.search(params, (err, experiences) => {
        this.searchDone(err, experiences, params, true);
      });
    }

          //sort hosts depending on user's choice
    if(this.hosts.length !== 0){
      this.sortHosts();
    }

    this.updateHostsList(isFallbackRequest); //updates the hosts list
    this.updateMarkersOnMap(); //updates markers on map

    if(this.hosts.length === 0){
      this.hostsContainer.textContent = this.hostsContainer.dataset.noHostsFound;
      this.hostsContainer.classList.add('font-semibold');
    }
    else{
      //replace by kiosk host url if on kiosk page
      if(this.searchWrapper.hasAttribute('data-kiosk-host-url')){
        this.hosts.forEach((host) => {
          host.url = this.searchWrapper.dataset.kioskHostUrl.replace('%id%', host.id);
        });
      }

      this.hostsContainer.classList.remove('font-semibold');
      this.hostsContainer.scrollTo(0, 0); //make sure we scroll full top when a new search is triggered
    }

    //reset main/secondary flags
    //they are checked before a search performs
    //so we need to reset them at the end of every search
    this.mainFiltersActive = false;
    this.secondaryFiltersActive = false;
    this.mainActiveFiltersList = [];

    this.hostsContainer.classList.remove('overlay-on'); //remove search overlay
  }

  getActiveFilters(){
    // let onMobile = window.matchMedia("(max-width: 640px)").matches;
    let params = {};
    params.numericFilters = [];
    params.facetFilters = [];

    this.getTypesOfExp(params.facetFilters);
    this.getLanguages(params.facetFilters);
    this.getNumberOfPersons(params.numericFilters);
    this.getPrice(params.numericFilters);
    this.getDays(params.facetFilters);

    //FILTERS THAT ARE IN MORE FILTERS WINDOW (DESKTOP)
    this.getTimesOfDay(params.numericFilters);
    this.getInstantBook(params.numericFilters);
    this.getReviews(params.numericFilters);
    this.getTypesOfHost(params.facetFilters);
    this.getServices(params.facetFilters);
    this.getCancellation(params.numericFilters);
    this.getVisibility(params.numericFilters);

    //if region, add the region ID
    if(this.wrapper.hasAttribute('data-region-id')){
      params.numericFilters.push("region_id = " + this.wrapper.dataset.regionId);
    }
    // else if(this.wrapper.hasAttribute('data-appellation-id')){
    //   params.facetFilters.push(['host.appellations.id:' + this.wrapper.dataset.appellationId]);
    // }

    if(this.searchWrapper.hasAttribute('data-hosts-to-filter')){
      this.addKioskHosts(params);
    }

    return params;
  }

  //used only for kiosk hosts
  addKioskHosts(params){
    let hostsToFilter = JSON.parse(this.searchWrapper.dataset.hostsToFilter);
    let hosts = [];
    hostsToFilter.forEach(host => {
      hosts.push('host.id:' + host);
    });

    params.facetFilters.push(hosts);
  }

  //used for fallback request when no initial results
  getBlankParams(){
    let pos = this.map.getCenter();
    let params = {
      query: '',
      hitsPerPage: 10, //
      attributesToRetrieve: '*',
      responseFields: '*',
      numericFilters: [],
      facetFilters: [],
      aroundLatLng: pos.lat + "," + pos.lng,
      aroundRadius: 200000 //200KM
    };

    //in case it is kiosk
    if(this.searchWrapper.hasAttribute('data-hosts-to-filter')){
      this.addKioskHosts(params);
    }

    return params;
  }

  getTypesOfExp(facetFilters){
    let typesOfExpArr = [];

    let typesOfExpActive = this.wrapper.querySelectorAll(".type-of-exp-filter:checked");
    if (typesOfExpActive) {
      typesOfExpActive.forEach(function (typeOfExp) {
        let typeOfExpID = typeOfExp.dataset.id;
        if (typeOfExpID) {
          typesOfExpArr.push('activitys:' + typeOfExpID);
        }
      });
    }

    if (typesOfExpArr.length > 0) {
      facetFilters.push(typesOfExpArr);
      this.mainFiltersActive = true;
      if(this.mainActiveFiltersList.indexOf('typeofexp') === -1){
        this.mainActiveFiltersList.push('typeofexp')
      }
    }
  }

  getLanguages(facetFilters){
    let languagesArr = [];

    let languagesActive = this.wrapper.querySelectorAll(".lang-filter:checked");
    if (languagesActive) {
      languagesActive.forEach(function (language) {
        let languageISO = language.dataset.iso;
        if (languageISO) {
          languagesArr.push('langs:' + languageISO);
        }
      });
    }

    if (languagesArr.length > 0) {
      facetFilters.push(languagesArr);
      this.mainFiltersActive = true;
      if(this.mainActiveFiltersList.indexOf('langs') === -1){
        this.mainActiveFiltersList.push('langs')
      }
    }
  }

  getNumberOfPersons(numericFilters){
    const adultsInput = this.wrapper.querySelector('#number-adults');
    const childrenInput = this.wrapper.querySelector('#number-children');

    if(adultsInput && childrenInput){
      let persons = parseInt(adultsInput.value) + parseInt(childrenInput.value);

      if (persons > 0) {
        numericFilters.push("booking_min <= " + persons);
        numericFilters.push("booking_max >= " + persons);
        this.mainFiltersActive = true;

        if(this.mainActiveFiltersList.indexOf('persons') === -1){
          this.mainActiveFiltersList.push('persons')
        }
      }
    }
  }

  getPrice(numericFilters){
    numericFilters.push('price_in_euros >= ' + this.minPrice);
    numericFilters.push('price_in_euros <= ' + this.maxPrice);

    if(this.maxPrice != this.hostsContainer.dataset.highestPrice || this.minPrice != 0){
      this.mainFiltersActive = true;
      if(this.mainActiveFiltersList.indexOf('price') === -1){
        this.mainActiveFiltersList.push('price')
      }
    }
  }

  getDays(facetFilters){
    let timeSlots = [];

    if (this.dateWrapper && this.dateWrapper.dataset.activeDays) {
      let activeDays = JSON.parse(this.dateWrapper.dataset.activeDays);
      if (activeDays) {
        activeDays.forEach(dayOfWeek => {
          if(dayOfWeek === 0){
            dayOfWeek = 7;
          }

          timeSlots.push("time_slots:" + dayOfWeek + "ma");
          timeSlots.push("time_slots:" + dayOfWeek + "m");
          timeSlots.push("time_slots:" + dayOfWeek + "a");
        });

        if (timeSlots.length) {
          facetFilters.push(timeSlots);
          this.mainFiltersActive = true;
          if(this.mainActiveFiltersList.indexOf('days') === -1){
            this.mainActiveFiltersList.push('days')
          }
        }
      }
    }
  }

  getReviews(numericFilters){
    let containerReviews = document.getElementById("container-reviews");
    if(!containerReviews) return;

    let buttonActive = containerReviews.querySelector("button.active");
    if(!buttonActive) return;

    let averageReview = parseFloat(buttonActive.dataset.averageReview);
    if (averageReview >= 0 && averageReview <= 5) {
      this.secondaryFiltersActive = true;
      numericFilters.push("host.average_review >= " + averageReview);
    }
  }

  getTypesOfHost(facetFilters){
    let typesOfHost = [];

    let typesOfHostActive = this.wrapper.querySelectorAll("#filters #typesOfHost .filter:checked");
    if (typesOfHostActive) {
      typesOfHostActive.forEach(function (typeOfHost) {
        let typeOfHostID = typeOfHost.dataset.id;
        if (typeOfHostID) {
          typesOfHost.push('categories:' + typeOfHostID);
        }
      });
    }

    if (typesOfHost.length > 0) {
      facetFilters.push(typesOfHost);
      this.secondaryFiltersActive = true;
    }
  }

  getServices(facetFilters){
    const _this = this;
    let services = [];

    let servicesActive = this.wrapper.querySelectorAll("#filters #services .filter:checked");
    if (servicesActive) {
      servicesActive.forEach(function (service) {
        let serviceID = service.dataset.id;
        if (serviceID) {
          services.push('services:' + serviceID);
          _this.secondaryFiltersActive = true;
        }
      });
    }

    if (services.length > 0) {
      facetFilters.push(services);
    }
  }

  getInstantBook(numericFilters){
    this.instantBook = this.wrapper.querySelector("#instantBook");
    if (this.instantBook && this.instantBook.checked) {
      numericFilters.push('booking_auto = 1');
      this.secondaryFiltersActive = true;
    }
  }

  getVisibility(numericFilters){
    const visibility = this.wrapper.querySelector("input[name='more-visibility']:checked");
    if (visibility && visibility.value === "private") {
      numericFilters.push("private = 1");
      this.secondaryFiltersActive = true;
    }
  }

  getCancellation(numericFilters){
    const visibility = this.wrapper.querySelector("input[name='cancellation']:checked");
    if (visibility && visibility.value === "full_refund_under_24") {
      numericFilters.push("full_refund_under_24 = 1");
      this.secondaryFiltersActive = true;
    }
  }

  getTimesOfDay(numericFilters){
    const _this = this;

    let timesOfDayActive = this.wrapper.querySelectorAll("#filters #timesOfDay .filter:checked");
    if (timesOfDayActive) {
      timesOfDayActive.forEach(function (timeOfDay) {
        let algoliaAttribute = timeOfDay.dataset.algoliaAttribute;
        if (algoliaAttribute) {
          numericFilters.push(algoliaAttribute + ' = 1');
          _this.secondaryFiltersActive = true;
        }
      });
    }
  }

  sortHosts(){
    // if(this.sortOption === 'relevance')
    //   this.hosts.sort(dynamicSort('id', 'ASC')); //algolia initial sort

    if(this.sortOption === 'popularity')
      this.hosts.sort(dynamicSort('bookedTimes', 'DESC'));

    // else if(this.sortOption === 'reviews'){
    //   this.hosts.sort(dynamicSort('count_review', 'DESC'));
    // }

    else if(this.sortOption === 'cheapest')
      this.hosts.sort(dynamicSort('lowest_price_in_euros', 'ASC'));

    else if(this.sortOption === 'most-expensive')
      this.hosts.sort(dynamicSort('lowest_price_in_euros', 'DESC'));
  }

  //if fallback request & if filters active > show the "no exact matches" + remove filters button
  buildNoExactMatchesBlock(){
    let removeFiltersButtonsBlock = '';

    if(this.mainFiltersActive)
      removeFiltersButtonsBlock = this.buildRemoveFiltersButtons();

    return `
      <div class="w-full md:px-2" id="no-results-block">
        <p class="font-semibold mt-4 text-lg">${this.searchWrapper.dataset.noExactMatchesText}</p>
        <p class="text-gray-500">${this.searchWrapper.dataset.tryChangingText}</p>
        <div class="flex flex-wrap items-center mt-4">
          <button class="btn btn-outline-black shadow-none text-black py-3 px-6 mr-3 sm:mr-4 mb-3 sm:mb-4" id="remove-all-filters">
            ${this.searchWrapper.dataset.removeAllFiltersText}
          </button>
          ${removeFiltersButtonsBlock}
        </div>
        <hr class="w-full my-6">
        <p class="font-semibold mb-5 text-lg">${this.searchWrapper.dataset.hostsNoExactMatchText}</p>
      </div>
    `;
  }

  //block with buttons like "remove price filter", "remove persons filter", ...
  buildRemoveFiltersButtons(){
    let html = '';
    this.mainActiveFiltersList.forEach(type => {
      let text = this.searchWrapper.getAttribute('data-remove-' + type);
      html += `<button class="btn btn-outline-black shadow-none text-black py-3 px-6 mr-3 sm:mr-4 mb-3 sm:mb-4 remove-filter-button"
                        data-type="${type}">${text}</button>`;
    });

    return html;
  }

  //bind click on remove buttons
  bindRemoveFiltersButtons(){
    const noResultsBlock = document.getElementById('no-results-block');
    if(noResultsBlock){
      //bind specific filter all buttons
      const removeFilterButtons = noResultsBlock.querySelectorAll('.remove-filter-button');
      if(removeFilterButtons){
        removeFilterButtons.forEach(button => {
          button.addEventListener('click', (e) => {
            const type = e.target.dataset.type;
            if(type === 'price'){
              this.clearPrice();
            }
            else if(type === 'typeofexp'){
              this.clearTypeOfExp();
            }
            else if(type === 'days'){
              this.initClearDate();
            }
            else if(type === 'persons'){
              this.clearPersons();
            }
            else if(type === 'langs'){
              this.clearLangs();
            }

            //compute new active filters count
            this.computeActiveFiltersCount();

            //remove the button
            e.target.remove();

            //trigger another search
            this.debouncedPerformSearch();
          });
        });
      }

      //bind remove all filters button
      const removeAllFiltersButton = noResultsBlock.querySelector('#remove-all-filters');
      if(removeAllFiltersButton){
        removeAllFiltersButton.addEventListener('click', (e) => {
          //clear all main filters
          this.clearPrice();
          this.clearTypeOfExp();
          this.initClearDate();
          this.clearPersons();
          this.clearLangs();

          //and clear all secondary filters
          this.clearSecondaryFilters();

          //compute new active filters count
          this.computeActiveFiltersCount();

          //remove the button
          e.target.remove();

          //trigger another search
          this.debouncedPerformSearch();
        });
      }
    }
  }

  //if fallback request & no filters active > show the "No hosts in that area yet..." block
  buildNoHostsInThatAreaBlock(){
    return `
      <div class="w-full md:px-2">
        <p class="font-semibold mt-4 text-lg">${this.searchWrapper.dataset.noHostsAreaText}</p>
        <p class="text-gray-500">${this.searchWrapper.dataset.currentlyNoHostsText}</p>
        <hr class="w-full my-6">
        <p class="font-semibold mb-5 text-lg">${this.searchWrapper.dataset.otherHostsText}</p>
      </div>
    `;
  }

  updateHostsList(isFallbackRequest = false){
    let html = "";

    //if fallback request
    if(isFallbackRequest && this.hosts.length > 0){
      //if main filters active > show the "no exact matches" + remove filters button
      if(this.mainFiltersActive || this.secondaryFiltersActive){
        html += this.buildNoExactMatchesBlock();
      }
      //else show the "No hosts in that area yet..." block
      else{
        html += this.buildNoHostsInThatAreaBlock();
      }
    }

    this.hosts.forEach((host) => {
      let priceFormat;
      // let rawPriceFormat;
      if(host.lowest_price_in_euros > 0){
        if(host.currency_iso != this.currencyInfos.iso)
          var amount = Math.ceil(host.lowest_price_in_euros * this.currencyInfos.rate);
        else
          var amount = host.lowest_price_in_euros * this.currencyInfos.rate;

        let price = new Intl.NumberFormat(this.currencyInfos.locale, { style: 'currency', currency: this.currencyInfos.iso }).format(amount);
        priceFormat = this.hostsContainer.dataset.from.replace("{0}", " <span>" + price + "</span> ");
        // rawPriceFormat = this.hostsContainer.dataset.from.replace("{0}", price);
      } else {
        priceFormat = "<span>" + this.hostsContainer.dataset.free + "</span>";
        // rawPriceFormat = this.hostsContainer.dataset.free;
      }

      //does the host has at least one instant book experience?
      let bookAuto = '';
      if(host.can_book_auto){
        bookAuto += `<span class="rounded border text-xs px-2 py-1 font-medium mr-2 mb-1">
                        <i class="icon icon-bolt text-red"></i>
                        ${this.hostsContainer.dataset.bookingAuto}
                      </span>`
      }

      let cultureType = '';
      if(typeof host.culture_type !== 'undefined' && host.culture_type !== null){
        let translation = this.hostsContainer.getAttribute(this.cultureTypes[host.culture_type - 1]);
        cultureType = `<span class="text-xs px-4 py-1 bg-green text-white rounded mb-1">${translation}</span>`;
      }

      let reviewBlockDesktop = '';
      let reviewBlockMobile = '';
      if(host.average_review){
          reviewBlockDesktop = `
          <div class="hidden sm:flex items-center text-black mt-1">
            <i class="icon icon-star text-yellow mr-2"></i>
            <div class="flex items-baseline mr-3">
              <span class="text-xl font-semibold leading-4">${host.average_review}</span>
              <span class="text-xs">/5</span>
            </div>
            <span class="text-gray-500 text-sm">(${this.hostsContainer.dataset.reviews.replace('{0}', host.count_review)})</span>
          </div>
        `;

        reviewBlockMobile = `
          <div class="sm:hidden flex items-center text-black mt-4">
            <i class="icon icon-star text-yellow mr-2"></i>
            <div class="flex items-baseline mr-3">
              <span class="text-xl font-semibold leading-4">${host.average_review}</span>
              <span class="text-xs">/5</span>
            </div>
            <span class="text-gray-500 text-sm">(${this.hostsContainer.dataset.reviews.replace('{0}', host.count_review)})</span>
          </div>
        `;
      }

      let appellations = '';
      if(typeof host.appellations !== 'undefined'){
          appellations = `
            <span class="mx-1">•</span>
            <div class="flex items-center no-text-overflow">
              <a href="${host.appellations[0].landing_url}" class="underline relative z-20">${host.appellations[0].name}</a>
          `;
          if(typeof host.appellations[1] !== 'undefined'){
            appellations += `,&nbsp<a href="${host.appellations[1].landing_url}" class="underline relative z-20">${host.appellations[1].name}</a>`;
          }

          appellations += `</div>`;
      }

      // Manage wishlist
      let wishes = JSON.parse(document.body.dataset.wishes);
      let hostsInWishlist =
            wishes?.hostsId?.length > 0
              ? wishes.hostsId
              : [];

      // var in body, default.twig
      let urlToggleWish = document.body.dataset.urlToggleLike;
      let urlContentnModalWish = document.body.dataset.urlContentModalWish;

      let inWishlist = hostsInWishlist.includes(host.id);
      let wish = `
        <button data-wishlist
                data-wishlist-active
                ${JSON.parse(document.body.dataset.connected) || "data-not-connected"}
                ${inWishlist ? "data-delete" : ""}
                data-model="Host" data-model-id="${host.id}"
                data-url="${urlToggleWish}"
                data-url-content-modal="${urlContentnModalWish}"
                class="btn-wishlist z-30 absolute rounded-full bg-white right-0 top-0 mt-4 mr-3 text-center py-1 w-10 h-10 sm:w-8 sm:h-8 text-black text-lg sm:text-sm shadow-lg">
            <i class="icon z-30 relative ${inWishlist ? "icon-heart-filled text-red" : "icon-heart"} font-semibold"></i>
        </button>
      `;

      // Manage slider images
      let sliderImages = '';
      if (host.experiences && host.experiences.length != 0) {
        sliderImages = `
          <div class="swiper w-full h-full absolute rounded-lg"
            data-autoplay="1400"
            data-per-view="1"
            data-per-view-xs="1"
            data-per-view-sm="1"
            data-per-view-lg="1"
            data-per-view-xl="1"
            data-per-view-wide-xl="1"
            data-per-view-ultrawide-xl="1">

            <div class="swiper-wrapper w-full h-full">
                <div class="swiper-slide w-full h-full">
                  <span data-redirect-go="${btoa(host.url)}" target="_blank" title="${host.name}" class="cursor-pointer w-full h-full">
                    <picture class="h-full w-full">
                      <img class="twic"
                            src="${host.media_cdn_url}/resize=2p"
                            data-src="${host.media_cdn_path}"
                            data-src-transform="cover">
                    </picture>
                  </span>
                </div>
        `;

        host.experiences.forEach((experience) => {
          sliderImages += `
            <div class="swiper-slide w-full h-full">
                <span data-redirect-go="${btoa(host.url)}" target="_blank" title="${host.name}" class="cursor-pointer w-full h-full">
                    <picture class="h-full w-full">
                        <img class="twic" alt="${experience.name}"
                            src="${experience.coverCdnUrl }}/resize=2p"
                            alt="${experience.name}"
                            data-src="${experience.coverCdnPath}"
                            data-src-transform="cover">
                    </picture>
                </span>
            </div>
          `
        });

        sliderImages += `
            </div>
            <div class="swiper-prev hover-effect host-thumbnail z-30 flex items-center justify-center shadow-lg absolute p-3 sm:p-2 rounded-full border bg-white cursor-pointer" style="top: calc(50% - 1rem); left: 0.75rem;">
                <i class="icon icon-caret-left text-lg sm:text-sm font-semibold"></i>
            </div>
            <div class="swiper-next hover-effect host-thumbnail z-30 flex items-center justify-center shadow-lg absolute p-3 sm:p-2 rounded-full border bg-white cursor-pointer" style="top: calc(50% - 1rem); right: 0.75rem;">
                <i class="icon icon-caret-right text-lg sm:text-sm font-semibold"></i>
            </div>
          </div>
        `;
      }

      //TO REMOVE
      host.content_algolia = host.content_algolia.slice(0, 147) + "...";

      let target = this.searchWrapper.hasAttribute('data-kiosk') ? '_self' : '_blank';
      const CDN_URL = this.hostsContainer.dataset.cdnUrl;

      let date = new Date();
      let thirtyDaysAgo = date.setDate(date.getDate() - 30);
      let thirtyDaysAgoUnixTimestamp = Math.floor(thirtyDaysAgo / 1000);
      let newHost = host.first_exp_published_timestamp >= thirtyDaysAgoUnixTimestamp ? `
        <span class="bg-transparent text-red border border-red rounded py-1 px-2 text-tiny align-middle uppercase mr-2 mb-1">${this.searchWrapper.dataset.textNew}</span>` : '';

      //build the card
      html += `
        <article class="card search-card-host border border-gray-300 sm:border-t-0 sm:border-l-0 sm:border-r-0 sm:pl-4 mb-8 sm:mb-0 flex flex-col sm:flex-row sm:items-center max-w-lg sm:max-w-full w-full text-left sm:py-3 overflow-visible mx-auto"
                data-host-id="${host.id}"
                data-autoplay-on-hover>
          <a href="${host.url}" target="${target}" title="${host.name}">${host.name}</a>
          <div class="absolute w-16 h-16 rounded-full host-brand sm:hidden z-30 border-2 border-white">
            <img class="twic h-full w-full rounded-full bg-white"
              src="${host.brand_cdn_url}/resize=2p"
              data-src="${host.brand_cdn_path}"
              data-src-transform="contain">
          </div>
          <header class="relative rounded-t sm:rounded-lg overflow-hidden flex-shrink-0 z-20">
            ${wish}
            ${sliderImages}
          </header>
          <div class="w-full flex flex-col sm:pl-6 p-4 py-8 mt-2 sm:py-4 card-content items-center sm:items-start overflow-hidden">
              <div class="flex items-baseline w-full justify-center sm:justify-start">
                <div class="flex flex-col items-center sm:items-start sm:mr-4">
                  <div class="font-semibold text-3xl sm:text-xl mb-3 text-center sm:text-left">
                    <h3 class="leading-7 text-3xl sm:text-xl inline">${host.name}</h3>
                    <img class="twic w-5 sm:w-4 h-auto object-contain inline-block mb-px z-40"
                          src="${CDN_URL}img/new_assets/icon_verified.svg">
                  </div>
                  <div class="flex flex-wrap items-center justify-center sm:justify-start">
                    ${newHost}
                    ${bookAuto}
                    ${cultureType}
                  </div>
                </div>
              </div>
              <p class="hidden sm:text-sm xl:text-base sm:block mb-2 mt-4 description">
                ${host.content_algolia}
              </p>
              <div class="hidden sm:flex items-center text-gray-500 mb-4 text-sm mt-auto">
                <a href="${host.region_url}" class="underline relative z-20">${host.region}</a>
                ${appellations}
              </div>
              <div class="flex flex-wrap items-center justify-center sm:justify-start text-gray-600 mt-4 sm:mt-auto">
                <div class="flex items-center mr-6 mt-1">
                  <i class="icon icon-tag mr-1"></i>
                  <span>${priceFormat}</span>
                </div>
                <div class="flex items-center mr-6 mt-1 ${host.maxPersons <= 0 ? 'hidden' : ''}">
                  <i class="icon icon-users mr-2"></i>
                  <span>${host.min_persons}-${host.max_persons}</span>
                </div>
                ${reviewBlockDesktop}
              </div>
              ${reviewBlockMobile}
          </div>
        </article>
      `;
    });

    this.hostsContainer.innerHTML = html;

    //init the mouser hover on cards
    this.initCardMouseHover();

    //if needed, bind the remove filter buttons
    this.bindRemoveFiltersButtons();

    // bind the wishlist buttons
    Wish.bindWishButtons();

    //init the swiper
    initSwiper();

    //initOffuscation
    let hostContainer = document.querySelector('#hosts-container');
    initOffuscation(hostContainer);
  }

  updateMarkersOnMap(){
    const _this = this;

    //Clears every current markers on map
    Object.values(this.markersPlaced).forEach(function (marker) {
      marker.remove();
    });

    this.hosts.forEach((host) => {
      let marker = document.createElement('div');
      marker.className = 'marker single-marker';

      if(host.currency != _this.currencyInfos.iso)
        var amount = Math.ceil(host.lowest_price_in_euros * _this.currencyInfos.rate);
      else
        var amount = host.lowest_price_in_euros * _this.currencyInfos.rate;

      //format the host lowest price with the current currency
      let formattedPrice = new Intl.NumberFormat(_this.currencyInfos.locale, {
        style: 'currency',
        currency: _this.currencyInfos.iso,
        minimumFractionDigits: 0
      }).format(amount);
      marker.textContent = formattedPrice;

      if (host.can_book_auto) { //if host has an exp that has booking auto mode, add the booking auto icon
        let icon = buildBookingAutoIcon();
        marker.appendChild(icon);
      }

      marker.addEventListener('click', function () {
        //setTimeout is used to make sure this is done after map click event (which removes highlighted markers)
        setTimeout(function () {
          marker.classList.toggle("highlighted");
        }, 100);
      });

      let popupOptions = {
        offset: 15,
        closeButton: false,
        // className: this.searchWrapper.classList.contains('fullscreen') ? 'on-mobile' : ''
      };
      let popup = new this.mapboxgl.Popup(popupOptions).setHTML(this.buildPopupHTML(host));

      //when the popup opens, scroll to the host card on the list
      popup.on("open", () => {
        const cardOnList = this.wrapper.querySelector(`[data-host-id='${host.id}']`);
        if(cardOnList){
          const alreadyHighlightedCard = this.hostsContainer.querySelector('.highlighted');
          if(alreadyHighlightedCard) alreadyHighlightedCard.classList.remove('highlighted');

          //we don't want to play the animation when an host list card is hovered
          // if(this.playOpenAnimation){
            //option for scrollIntoView
            //if the card is first/last, we don't want center, since it will offset the map
          let blockOption = 'center';
          if(cardOnList.nextElementSibling === null) blockOption = 'end';
          // else if(cardOnList.previousElementSibling === null) blockOption = 'nearest';
          cardOnList.scrollIntoView({ behavior: 'smooth', block: blockOption});
          // }

          cardOnList.classList.add('highlighted');
        }

        // this.playOpenAnimation = true;
      });

      _this.markersPlaced[host.id] = new this.mapboxgl.Marker(marker)
        .setLngLat([host.lng, host.lat])
        .setPopup(popup)
        .addTo(_this.map);
    });
  }

  buildPopupHTML(host){
    let priceFormat;
    // let rawPriceFormat;
    if(host.lowest_price_in_euros > 0){
      if(host.currency_iso != this.currencyInfos.iso)
        var amount = Math.ceil(host.lowest_price_in_euros * this.currencyInfos.rate);
      else
        var amount = host.lowest_price_in_euros * this.currencyInfos.rate;

      let price = new Intl.NumberFormat(this.currencyInfos.locale, { style: 'currency', currency: this.currencyInfos.iso }).format(amount);
      priceFormat = this.hostsContainer.dataset.from.replace("{0}", " <span class='text-red md:text-black md:font-semibold'>" + price + "</span> ");
    } else {
      priceFormat = "<span class='text-red md:text-black md:font-semibold'>" + this.hostsContainer.dataset.free + "</span>";
    }

    let html = `<article class="card card-host on-popup bg-transparent max-w-xs w-80 overflow-hidden text-left" data-host-id="${host.id}">
    <header class="relative rounded overflow-hidden mb-3 w-full">
        <picture class="h-full w-full">
            <img class="twic h-full w-full"
                src="${host.media_cdn_url}/resize=2p"
                data-src="${host.media_cdn_path}"
                data-src-transform="cover">
        </picture>

        <div class="absolute bottom-0 p-4 w-full whitespace-normal card__logo">
            <img class="object-contain bg-white rounded-full w-8 h-8 mr-2 border border-champagne twic"
                    src="${host.brand_cdn_url}/resize=2p"
                    alt="${host.brand_alt ?? host.name}"
                    data-src="${host.brand_cdn_path}" data-src-transform="contain">
        </div>
    </header>
    <div class="p-4 pt-2 content">
    <a href="${host.url}" target="_blank" class="host-click-from-map host-name font-semibold text-xl mt-1 stretched-link text-black visible focus:outline-none"
        title="${host.name}">${host.name}</a>
    <p class="lg:font-normal text-gray-500 my-2 price">${priceFormat}</p>`;

    if(host.average_review > 0 || host.can_book_auto){
      html += `<div class="flex flex-wrap items-center justify-start mt-2">`;
      if(host.average_review > 0){
        html += `<div class="rounded border uppercase text-xs px-2 py-1 mr-2 mt-1">
                      <i class="icon icon-star text-yellow -mt-1"></i>
                      <span class="font-semibold">${host.average_review}</span>
                      <span class="text-gray-500"> (${host.count_review})</span>
                  </div>`;
      }
      if(host.can_book_auto){
        html += `<div class="rounded border uppercase text-xs px-2 py-1 mt-1 font-semibold">
                    <i class="icon icon-bolt text-red -mt-1"></i>
                    <span class="hidden xs:inline">${this.hostsContainer.dataset.bookingAuto}</span>
                </div>`;
      }

      html += `</div>`;
    }

    html += `</div>
    </article>`;

    return html;
  }

  //Toggle Map / Host List layout when clicking show map / show list
  initToggleMap(){
    this.toggleLayoutButton.addEventListener('click', () => {
      this.mapContainer.classList.toggle("hidden");
      this.hostsContainer.classList.toggle("hidden");

      const span = this.toggleLayoutButton.querySelector("span");
      if (span) {
        let text = "";
        if (this.mapContainer.classList.contains("hidden")) {
          //Showing list of hosts
          text = this.toggleLayoutButton.dataset.showMap;
          this.searchWrapper.classList.remove('fullscreen');
          this.toggleLayoutWrapper.classList.remove('increase-z-index');

          //change icon
          if(this.iconToggleLayout){
            this.iconToggleLayout.classList.toggle('icon-list');
            this.iconToggleLayout.classList.toggle('icon-location');
          }

          let onMobile = window.matchMedia("(max-width: 1024px)").matches;
          if(onMobile){
            let btnSwapToHosts = document.querySelector('#swap-to-hosts');
            if(btnSwapToHosts) btnSwapToHosts.classList.add('hidden');
          }
        }
        else {
          //Showing Map
          text = this.toggleLayoutButton.dataset.showList;

          //change icon
          if(this.iconToggleLayout){
            this.iconToggleLayout.classList.toggle('icon-location');
            this.iconToggleLayout.classList.toggle('icon-list');
          }

          window.setTimeout(() => {
            this.map.resize();
          }, 100);

          let onMobile = window.matchMedia("(max-width: 1024px)").matches;
          if(onMobile){
            this.searchWrapper.classList.add('fullscreen');
            this.toggleLayoutWrapper.classList.add('increase-z-index');

            let btnSwapToHosts = document.querySelector('#swap-to-hosts');
            if(btnSwapToHosts){
              btnSwapToHosts.classList.remove('hidden');

              if (btnSwapToHosts.classList.contains('binded')) return;

              btnSwapToHosts.addEventListener('click', () => {
                btnSwapToHosts.classList.toggle('hidden');
                this.toggleLayoutButton.click();
              });
            }
          }
        }
        span.textContent = text;
      }
    });
  }

  initAroundMe(){
    this.triggerAroundMeButton.addEventListener('click', () => {
      navigator.geolocation.getCurrentPosition((myPosition) => {
        let lat = myPosition.coords.latitude;
        let lng = myPosition.coords.longitude;

        let options = {
            center: [lng, lat],
            zoom: 11,
            speed: 2.5
        };

        if(!this.aroundMeMarkerPlaced){
          let div = document.createElement('div');
          div.className = 'search-result-marker';
          new this.mapboxgl.Marker({
            element: div
          })
          .setLngLat([lng, lat])
          .addTo(this.map);

          this.aroundMeMarkerPlaced = true;
        }

        this.map.jumpTo(options);
        this.debouncedPerformSearch();
      });
    });
  }

  initCenterPointOnKiosk(){
    let div = document.createElement('div');
    div.className = 'search-result-marker';

    new this.mapboxgl.Marker({
      element: div
    })
    .setLngLat([this.mapContainer.dataset.lng, this.mapContainer.dataset.lat])
    .addTo(this.map);
  }

  initCloseMoreFiltersButtons(){
    this.closeFiltersButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.removeOverlay();
        this.removeMoreFilters();
      });
    });
  }

  initSorting(){
    this.sortOptions.forEach(option => {
      option.addEventListener('change', () => {
        if(option.checked){
          //change the button text to match the option chosen
          this.sortButtonText.textContent = option.dataset.text;
          this.sortOption = option.value;

          //close the dropdown
          const dropdown = option.closest('.dropdown');
          if(dropdown) dropdown.classList.remove('active');

          //sort hosts
          this.sortHosts();

          //then reorder host cards in the list
          if(this.hosts.length !== 0){
            this.updateHostsList();
            this.hostsContainer.scrollTo(0, 0); //make sure we scroll full top of the hosts card list
          }
        }
      });
    });
  }

  initCardMouseHover(){
    const listCards = this.hostsContainer.querySelectorAll('.search-card-host:not(.hover-binded)');
    if(listCards){
      listCards.forEach(card => {
        card.addEventListener('mouseenter', () => {
          //remove the highlighted on a previous card (if there is one)
          const alreadyHighlightedCard = this.hostsContainer.querySelector('.highlighted');
          if(alreadyHighlightedCard) alreadyHighlightedCard.classList.remove('highlighted');

          //highlighted the current card that is hovered
          card.classList.add('highlighted');

          //also highlight the marker on the map
          this.openPopup(card.dataset.hostId);
        });

        card.classList.add('hover-binded');
      });
    }
  }

  //trigger the marker popup corresponding to the hovered host in the list
  openPopup(hostId){
    let marker = this.markersPlaced[hostId];
    // let popup;

    if (marker) {
      // popup = marker.getPopup();

      //remove the highlight class on a potential existing active marker
      const markerHighlighted = this.mapContainer.querySelector(".marker.highlighted");
      if (markerHighlighted) {
        markerHighlighted.classList.toggle("highlighted");
      }

      //highlight the marker corresponding to the host card hovered
      marker._element.classList.toggle("highlighted");
    }

    //make sure to not scroll to the already hovered host card (might cause unwanted UX behavior)
    // this.playOpenAnimation = false;

    //Close every popups
    // Object.values(this.markersPlaced).forEach((markerTemp) => {
    //   let markerPopup = markerTemp.getPopup();
    //   if (markerPopup && markerPopup.isOpen()) {
    //     markerTemp.togglePopup();
    //   }
    // });

    // if (marker && popup && !popup.isOpen()) {
    //   marker.togglePopup();
    // }
  }

  //trigger the compute of active filters in the more filters window when a filter changes
  initComputeActiveFilters(){
    const inputs = this.moreFiltersWindow.querySelectorAll('input:not(.ignore-compute-active-filters)');
    if(inputs){
      inputs.forEach(input => {
        input.addEventListener('change', () => {
          this.computeActiveFiltersCount();
        });
      });

      this.computeActiveFiltersCount();
    }
  }

  //compute active filters in the more filters window
  computeActiveFiltersCount(){
    let activeFiltersCount = this.moreFiltersWindow.querySelectorAll('input:not(.ignore-compute-active-filters):checked').length;
    if(this.currentDateSelected !== null) activeFiltersCount++;
    if(this.adults > 0 || this.children > 0) activeFiltersCount++;
    if(this.maxPrice != this.hostsContainer.dataset.highestPrice || this.minPrice != 0) activeFiltersCount++;

    if(activeFiltersCount > 0){
      this.moreFiltersButton.classList.add('active');
      if(this.moreFiltersCountActiveElement){
        this.moreFiltersCountActiveElement.innerHTML = '&nbsp• ' + activeFiltersCount;
        this.moreFiltersCountActiveElement.classList.remove('hidden');
      }
    }
    else{
      if(this.moreFiltersCountActiveElement)
        this.moreFiltersCountActiveElement.classList.add('hidden');

      this.moreFiltersButton.classList.remove('active');
    }
  }

  //we check if every experience is actually available (has at least one timeslot opened at the specified date) - called when a search is triggered with a date filter
  checkAvailability(experiences){
    let experience_ids = [];
    experiences.forEach(function(experience){
      experience_ids.push(experience.id);
    });

    let month = '' + (this.currentDateSelected.getMonth() + 1);
    let day = '' + this.currentDateSelected.getDate();
    if (month.length < 2){
      month = '0' + month;
    }
    if (day.length < 2){
      day = '0' + day;
    }
    let currentDateSelectedFormatted = [this.currentDateSelected.getFullYear(), month, day].join('-');

    let formData = new FormData();
    formData.append('date', currentDateSelectedFormatted);
    formData.append('experience_ids', JSON.stringify(experience_ids));

    const morning = this.wrapper.querySelector("input[data-algolia-attribute='morning']");
    const afternoon = this.wrapper.querySelector("input[data-algolia-attribute='afternoon']");
    if(morning && afternoon){
      formData.append('morning', morning.checked);
      formData.append('afternoon', afternoon.checked);
    }

    ajax(this.hostsContainer.dataset.filterByDate, function(data){
      let i = experiences.length;
      while (i--) {
          if (data.experiences_available.indexOf(experiences[i].id) === -1) {
            experiences.splice(i, 1);
          }
      }
    }, 'POST', formData, {
      "X-CSRF-Token": document.querySelector('meta[name="csrfToken"]')?.content
    }, false, true);
  }

  updateSession(){
    const data = new FormData();
    data.append('adults', this.adults);
    data.append('children', this.children);
    data.append('from', this.globalFilterDate instanceof Date ? dateToString(this.globalFilterDate) : '');
    data.append('langs', JSON.stringify(this.langs));

    ajax(this.searchWrapper.dataset.sessionUrl, function () { }, 'POST', data, {
      "X-CSRF-Token": document.querySelector('meta[name="csrfToken"]')?.content,
    });
  }
}

export default MapHostSearch;
